import { Controller } from 'stimulus';

// Multi-select SGG project search.
// Shows name + ID in the UI; stores comma-separated SGG IDs (or free-text values) in the hidden project field.
export default class extends Controller {
  static targets = ['value', 'query', 'results', 'selected', 'control'];

  connect() {
    this.url = this.data.get('url') || '/dashboard/sgg_search';
    this.labels = {};
    this.activeIndex = -1;
    this._timer = null;
    this._requestId = 0;
    this.form = this.element.closest('form');

    this._onDocClick = (e) => {
      if (!this.element.contains(e.target)) {
        this.commitQuery();
        this.closeDropdown();
      }
    };
    this._onPick = (e) => {
      const option = e.target.closest('[data-sgg-value]');
      if (!option || !this.resultsTarget.contains(option)) return;
      e.preventDefault();
      this.add(option.dataset.sggValue, option.dataset.sggLabel);
    };
    this._onRemove = (e) => {
      const btn = e.target.closest('[data-sgg-remove]');
      if (!btn || !this.selectedTarget.contains(btn)) return;
      e.preventDefault();
      e.stopPropagation();
      this.remove(btn.dataset.sggValue);
    };
    this._onFormSubmit = () => this.commitQuery();

    document.addEventListener('click', this._onDocClick);
    this.resultsTarget.addEventListener('mousedown', this._onPick);
    this.selectedTarget.addEventListener('click', this._onRemove);
    if (this.form) this.form.addEventListener('submit', this._onFormSubmit);

    this.renderChips();
    this.loadLabelsForSelected();
  }

  disconnect() {
    document.removeEventListener('click', this._onDocClick);
    this.resultsTarget.removeEventListener('mousedown', this._onPick);
    this.selectedTarget.removeEventListener('click', this._onRemove);
    if (this.form) this.form.removeEventListener('submit', this._onFormSubmit);
    clearTimeout(this._timer);
  }

  // --- UI actions (from the template) ---

  focusSearch(event) {
    if (event.target.closest('[data-sgg-remove], .sgg-project__results')) return;
    this.queryTarget.focus();
  }

  onFocus() {
    this.search();
  }

  onQuery() {
    clearTimeout(this._timer);
    this.showStatus('Searching projects…');
    this._timer = setTimeout(() => this.search(), 200);
  }

  onKeydown(event) {
    const options = this.optionElements();

    if (event.key === 'Escape') {
      this.closeDropdown();
      return;
    }

    if (event.key === 'Backspace' && !this.queryTarget.value) {
      const ids = this.ids();
      if (ids.length) this.remove(ids[ids.length - 1]);
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      const query = this.queryTarget.value.trim();
      const option = this.activeIndex >= 0 ? options[this.activeIndex] : null;

      if (option) {
        this.add(option.dataset.sggValue, option.dataset.sggLabel);
      } else if (query) {
        // Free-text / custom project value when nothing is highlighted
        this.add(query);
      } else if (options[0]) {
        this.add(options[0].dataset.sggValue, options[0].dataset.sggLabel);
      }
      return;
    }

    if (!options.length) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.activeIndex = Math.min(this.activeIndex + 1, options.length - 1);
      this.markActive(options);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.activeIndex = Math.max(this.activeIndex - 1, 0);
      this.markActive(options);
    }
  }

  // Persist typed text that was never picked from the dropdown
  commitQuery() {
    const query = this.queryTarget.value.trim();
    if (query) this.add(query, query, { keepFocus: false });
  }

  // Called when lifecycle modal appends to the hidden project field
  syncFromValue() {
    this.renderChips();
  }

  // --- Search ---

  async search() {
    const q = this.queryTarget.value.trim();
    const requestId = ++this._requestId;
    this.showStatus('Searching projects…');

    try {
      const items = await this.fetchSggs(q);
      if (requestId !== this._requestId) return;
      this.renderOptions(items, q);
    } catch (error) {
      if (requestId !== this._requestId) return;
      console.error('SGG search failed', error);
      this.showStatus('Unable to load projects. Please try again.');
    }
  }

  async loadLabelsForSelected() {
    if (!this.ids().length) return;

    try {
      const items = await this.fetchSggs('');
      items.forEach((item) => {
        if (item && item.value != null) {
          this.labels[String(item.value)] = item.label || String(item.value);
        }
      });
      this.renderChips();
    } catch (_) {
      // Chips still show IDs if names cannot be loaded
    }
  }

  async fetchSggs(query) {
    const response = await fetch(`${this.url}?q=${encodeURIComponent(query)}`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin'
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);

    const data = await response.json();
    return Array.isArray(data) ? data : [];
  }

  // --- Selection (IDs are what get saved) ---

  add(value, label, { keepFocus = true } = {}) {
    const id = String(value || '').trim();
    if (!id) return;

    if (label) this.labels[id] = label;

    const ids = this.ids();
    if (!ids.includes(id)) ids.push(id);
    this.save(ids);

    this.queryTarget.value = '';
    if (keepFocus) {
      this.queryTarget.focus();
      this.search();
    } else {
      this.closeDropdown();
    }
  }

  remove(value) {
    const id = String(value || '').trim();
    if (!id) return;
    this.save(this.ids().filter((current) => current !== id));
  }

  save(ids) {
    this.valueTarget.value = ids.join(', ');
    this.renderChips();
    this.valueTarget.dispatchEvent(new Event('input', { bubbles: true }));
  }

  ids() {
    return this.valueTarget.value.split(',').map((v) => v.trim()).filter(Boolean);
  }

  // --- Render ---

  renderChips() {
    const ids = this.ids();

    this.selectedTarget.innerHTML = ids.map((id) => {
      const name = this.labels[id];
      const isNumericId = /^\d+$/.test(id);
      const title = name && name !== id ? `${name} (ID ${id})` : (isNumericId ? `ID ${id}` : id);
      const display = name && name !== id
        ? name
        : (isNumericId ? `SGG ID ${id}` : id);

      return `
        <span class="sgg-project__chip" title="${this.escape(title)}">
          <span class="sgg-project__chip-id">${this.escape(display)}</span>
          <button type="button" class="sgg-project__chip-remove" data-sgg-remove data-sgg-value="${this.escape(id)}" aria-label="Remove project ${this.escape(id)}">×</button>
        </span>
      `;
    }).join('');

    this.queryTarget.placeholder = ids.length ? 'Search more projects…' : 'Type to search projects…';
  }

  renderOptions(items, query) {
    const selected = new Set(this.ids());
    const available = items.filter((item) => !selected.has(String(item.value)));
    this.activeIndex = -1;
    const q = (query || '').trim();
    const alreadySelected = q && selected.has(q);
    const exactMatch = available.some((item) => String(item.value) === q || String(item.label || '') === q);

    if (!available.length && !q) {
      this.showStatus('No projects available');
      return;
    }

    const optionsHtml = available.map((item) => {
      const id = String(item.value);
      const label = item.label || id;
      this.labels[id] = label;

      return `
        <button type="button" class="sgg-project__option" role="option" data-sgg-value="${this.escape(id)}" data-sgg-label="${this.escape(label)}">
          <span class="sgg-project__option-label">${this.escape(label)}</span>
          <span class="sgg-project__option-id">ID ${this.escape(id)}</span>
        </button>
      `;
    }).join('');

    // Offer free-text add when the typed value is not an exact SGG match
    const addCustomHtml = (q && !alreadySelected && !exactMatch)
      ? `
        <button type="button" class="sgg-project__option sgg-project__option--custom" role="option" data-sgg-value="${this.escape(q)}" data-sgg-label="${this.escape(q)}">
          <span class="sgg-project__option-label">Add “${this.escape(q)}”</span>
          <span class="sgg-project__option-id">custom value</span>
        </button>
      `
      : '';

    if (!optionsHtml && !addCustomHtml) {
      this.showStatus(alreadySelected ? `“${q}” is already selected` : `No projects found for “${q}”`);
      return;
    }

    this.resultsTarget.innerHTML = `${optionsHtml}${addCustomHtml}`;
    this.openDropdown();
  }

  showStatus(text) {
    this.resultsTarget.innerHTML = `<div class="sgg-project__empty">${this.escape(text)}</div>`;
    this.openDropdown();
  }

  openDropdown() {
    this.resultsTarget.classList.remove('-state-hidden');
    this.controlTarget.classList.add('is-open');
  }

  closeDropdown() {
    this.resultsTarget.classList.add('-state-hidden');
    this.controlTarget.classList.remove('is-open');
    this.activeIndex = -1;
  }

  optionElements() {
    return Array.from(this.resultsTarget.querySelectorAll('[data-sgg-value]'));
  }

  markActive(options) {
    options.forEach((el, i) => el.classList.toggle('is-active', i === this.activeIndex));
    const active = options[this.activeIndex];
    if (active) active.scrollIntoView({ block: 'nearest' });
  }

  escape(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }
}
